'use client';

import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';

import { ReactComponent as MailIcon } from '@/public/svgs/global/Icons/Paper_Plane.svg';
import AccountFieldTooltip from '@/app/account/_components/AccountFieldTooltip';

import styles from './AccountInput.module.scss';

type AccountInputProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: ReactNode;
  hasIcon?: boolean;
  hasTooltip?: boolean;
  hideLabel?: boolean;
  tooltipText?: string;
  leadingIcon?: ReactNode;
  endAdornment?: ReactNode;
  fieldClassName?: string;
};

export default function AccountInput({
  id,
  label,
  error,
  hasIcon = false,
  hasTooltip = false,
  hideLabel = false,
  tooltipText = 'More information',
  leadingIcon,
  endAdornment,
  fieldClassName,
  className,
  'aria-describedby': ariaDescribedBy,
  ...inputProps
}: AccountInputProps)
{
  const generatedId = useId();
  const inputId = id ?? `account-input-${ generatedId.replace(/:/g, '') }`;
  const errorId = `${ inputId }-error`;
  const describedBy = [ariaDescribedBy, error ? errorId : undefined].filter(Boolean).join(' ') || undefined;

  return (
    <div className={ clsx(styles.field, fieldClassName) }>
      <div className={ clsx(styles.labelRow, hideLabel && styles.labelRowHidden) }>
        <label htmlFor={ inputId }>{ label }</label>

        { hasTooltip && <AccountFieldTooltip tooltipText={ tooltipText } /> }
      </div>

      <div className={ clsx(styles.inputContainer, error && styles.inputError) }>
        { hasIcon &&
          <span className={ styles.leadingIcon } aria-hidden={ !leadingIcon }>
            { leadingIcon ?? <MailIcon /> }
          </span>
        }

        <input
          { ...inputProps }
          id={ inputId }
          aria-describedby={ describedBy }
          aria-invalid={ error ? true : inputProps['aria-invalid'] }
          className={ clsx(styles.input, className) }
        />

        { endAdornment &&
          <span className={ styles.endAdornment }>
            { endAdornment }
          </span>
        }
      </div>

      { error &&
        <p id={ errorId } className={ styles.error } role='alert'>
          { error }
        </p>
      }
    </div>
  );
}
